(ns frontend.view.settings2.surveys.question.question-creator
  (:require
   [common.languages :as languages]
   [common.localization :as loc]
   [common.schema.entities.survey.survey-question.schema :as survey-question]
   [common.schema.entities.survey.survey-question.options :as survey-question-options]
   [common.util :as cu]
   [frontend.view.settings2.surveys.events :as survey-events]
   [frontend.view.settings2.surveys.question.events :as events]
   [frontend.view.settings2.surveys.question.sub :as sub]
   [oops.core :as oops]
   [re-frame.core :as rf]
   [reagent.core :as r]
   [ui.components.button.view :as button]
   [ui.components.calculi-tree.view :as calculi-tree]
   [ui.components.ck-editor.view :as ck-editor]
   [ui.components.input.checkbox-group :as checkbox-group]
   [ui.components.modal.style :as modal-style]
   [ui.components.modal.view :as modal]
   [ui.components.reorder-controls.view :as reorder-controls]
   [ui.components.tab-strip.view :as tab-strip]
   [ui.components.text.view :as text]
   [ui.dom-globals :as dg]
   [ui.editors.data-input :as data-input]
   [ui.editors.labelled-input.view :as labelled-input]
   [ui.state.named-values :as named-values]
   [ui.util :as uu]))

(defn data-type-select
  [question]
  (let [options (->> survey-question-options/creatable-data-types
                     (map (fn [k]
                            {:value k
                             :label (survey-question/data-type-label loc/tr k)}))
                     (sort-by :label)
                     (vec))]
    [labelled-input/view
     {:data-type   :data.type/keyword
      :mandatory?  true
      :value       (->> options
                        (cu/filter-by :value (:survey-question/data-type question))
                        (first))
      :on-change   (fn [v]
                     (rf/dispatch [::events/set-data-type v]))
      :placeholder (loc/tr :select-data-type)
      :options     options
      :label       (loc/tr :data-type)}]))

(defn option-list-select
  [question]
  (let [option-list-options @(rf/subscribe [::sub/option-list-options])]
    [labelled-input/view
     {:data-type :data.type/keyword
      :value     (->> option-list-options
                      (cu/filter-by :value (:survey-question/option-list question))
                      (first))
      :label     (loc/tr :select-option-list)
      :options   option-list-options
      :on-change (fn [v]
                   (rf/dispatch [::events/set-option-list v]))}]))

(defn calculation-rule-editor
  [question-options question]
  [calculi-tree/calculation-builder
   {:node          (first (:survey-question/calculation-rules question))
    :tree-path     [::sub/survey-question-changes :survey-question/calculation-rules 0]
    :field-options question-options}])

(def file-type-options-with-custom-labels
  (->> survey-question-options/file-type-options
       (mapv (fn [opt]
               (assoc opt :label (loc/tr (keyword "survey-file-type" (name (:value opt)))))))))

(defn file-meta-inputs
  [question]
  [:<>
   [labelled-input/view
    {:label       (loc/tr :file-multiple-input)
     :mandatory?  true
     :data-type   :data.type/boolean
     :editor-type :editor.type/radio
     :options     [{:value true
                    :label (loc/tr :file-allow-multiple)}
                   {:value false
                    :label (loc/tr :file-disallow-multiple)}]
     :value       (:survey-question/multi-file? question)
     :on-change   (fn [v]
                    (rf/dispatch [::events/set-multi-file? v]))}]
   [labelled-input/view
    {:label       (loc/tr :file-input-types)
     :editor-type :editor.type/checkboxes
     :mandatory?  true
     :data-type   :data.type/keyword-multi
     :value       (:survey-question/accepted-file-types question)
     :options     file-type-options-with-custom-labels
     :on-change   (fn [v]
                    (js/console.log "Set accepted types : " v)
                    (rf/dispatch [::events/set-file-types v]))}]])

(defn unique-identifier-input
  [question]
  [labelled-input/view
   {:label       "Unique identifier?"
    :data-type   :data.type/boolean
    :editor-type :editor.type/checkbox
    :options     [{:value true
                   :label "Yes"}
                  {:value false
                   :label "No"}]
    :value       (:survey-question/unique-identifier? question)
    :on-change   (fn [v]
                   (rf/dispatch [::events/set-unique-identifier? v]))}])

(defn localized-label-input
  [{:keys [lang] :as lang-question}]
  [labelled-input/view
   {:data-type  :data.type/text-simple
    :mandatory? true
    :value      (get-in lang-question [:survey-question/label-translations lang])
    :on-blur    (fn [v]
                  (rf/dispatch [::events/set-label lang v]))
    :label      [:span
                 (loc/tr :question-label)
                 (when (not= :ENG lang)
                   [:span
                    (str " (ENG - "
                         (get-in lang-question [:survey-question/label-translations :ENG])
                         " )")])]
    :dom-id     "survey-question-creator-localized-label"}])

(defn localized-info-input
  [{:keys [lang] :as question}]
  [labelled-input/view
   {:label         (loc/tr :survey-question-info-label)
    :data-type     :data.type/text-richtext
    :toolbar       ck-editor/toolbar-no-indent
    :id            (str "survey-question-creator-localized-info-" lang)
    :border?       true
    :focus-border? true
    :value         (get-in question [:survey-question/info-translations lang])
    :on-blur       #(rf/dispatch [::events/set-info lang %])}])

(defn keywords-input
  [question]
  [:div
   {:class "flex flex-col gap-1 relative"}
   [labelled-input/label-elem
    {:label (loc/tr :survey-question-keywords)}]
   (when-let [codes (get-in question [:survey-question/keywords])]
     (into [:ul
            {:class "flex gap-1"}]
           (->> codes
                (map (fn [k]
                       [:li
                        {:class "py-1 px-2 inline-flex items-center gap-2 bg-base-gloomy rounded-full text-xs"}
                        k
                        [:i.fal.fa-times-circle
                         {:class    "cursor-pointer hover:text-base-regular-text-accent"
                          :on-click #(rf/dispatch [::events/remove-keyword k])}]]))
                (doall))))
   [data-input/view
    {:data-type   :data.type/text-simple
     :id          "survey-question-creator-keywords"
     :value       (get-in question [:survey-question/temp-keyword])
     :on-change   (fn [change]
                    (rf/dispatch [::events/input-keyword change]))
     :on-key-down (fn [e]
                    (let [key (dg/event-key-code e)]
                      (when (= key 13)
                        (rf/dispatch [::events/set-keyword])
                        (when-let [target (oops/oget e "currentTarget")]
                          (oops/ocall target "blur")
                          (js/setTimeout (fn [_]
                                           (oops/ocall target "focus"))
                                         20)))))
     :on-blur     #(rf/dispatch [::events/set-keyword])}]])

(defn data-unit-input
  [question]
  [labelled-input/view
   {:label     (loc/tr :survey-question/data-unit)
    :data-type :data.type/text-simple
    :value     (get-in question [:survey-question/data-unit])
    :on-change #(rf/dispatch [::events/set-data-unit %])}])

(defn option-management
  [{:keys [lang] :as question}]
  (let [opt-count (count (:survey-question/options question))]
    [:div
     {:class "flex flex-col gap-3 relative"}
     [labelled-input/label-elem
      {:label (loc/tr :option-kw-list)}]
     [:table
      {:class "table-auto"}
      [:thead
       [:tr
        (when (not= :ENG lang)
          [:th
           (loc/tr :english-version)])
        [:th
         {:class "w-full"}
         (loc/tr :language-version [(languages/get-label lang)])]
        [:th
         {:class "w-auto"}
         ""]]]
      [:tbody
       (->> question
            :survey-question/options
            (map-indexed
             (fn [idx opt]
               ^{:key (uu/react-key opt)}
               [:tr
                (when (not= :ENG lang)
                  [:td
                   {:class "whitespace-nowrap pe-2"}
                   (str (get-in opt [:survey-question-option/label-translations :ENG]) "(EN)")])
                [:td
                 [data-input/view
                  {:data-type   :data.type/text-simple
                   :full-width? true
                   :value       (get-in opt [:survey-question-option/label-translations lang])
                   :on-blur     #(rf/dispatch [::events/set-question-option-localization lang idx %])}]]
                [:td
                 ;; `:disabled` was passed here where the button reads
                 ;; `:disabled?`, so the first option's move-up and move-to-top
                 ;; were never actually disabled. The shared component takes
                 ;; `first?`/`last?` and gets it right once.
                 [reorder-controls/view
                  {:first?         (zero? idx)
                   :last?          (= (inc idx) opt-count)
                   :remove-label   (loc/tr :survey-block-editor/remove-option)
                   :on-move-to-top #(rf/dispatch [::events/move-first-option idx])
                   :on-move-up     #(rf/dispatch [::events/move-up-option idx])
                   :on-move-down   #(rf/dispatch [::events/move-down-option idx])
                   :on-remove      #(rf/dispatch [::events/remove-option idx])}]]])))]]
     [:div
      [button/sm-primary-solid
       {:on-click #(rf/dispatch [::events/add-option])}
       [:i.fal.fa-plus]
       (loc/tr :add)]]]))

(defn external-id
  [question]
  ;; The question-battery options are named-values, which the new grid does not
  ;; bulk-sync - ask for them once when this section mounts (idempotent) and the
  ;; subscription below fills in when they land.
  (r/with-let [_ (rf/dispatch [::named-values/ensure-loaded
                               :survey-question-ext-id/question-battery])]
    (let [question-battery-options @(rf/subscribe [::sub/question-battery-options])]
    [:div [:div
           {:class "flex flex-col gap-3 relative"}
           [labelled-input/label-elem
            {:label "External IDs"}]
           [:div
            (->> question
                 :survey-question/external-ids
                 (map-indexed
                  (fn [idx opt]
                    ^{:key (uu/react-key opt)}

                    [:div
                     {:class "flex flex-row gap-2 items-center mb-2"}
                     [labelled-input/view
                      {:data-type :data.type/keyword
                       :value     (->> question-battery-options
                                       (cu/filter-by :value (get-in opt [:survey-question-ext-id/question-battery :db/id]))
                                       (first))
                       :label     "Question battery"
                       :options   question-battery-options
                       :on-change (fn [v]
                                    (rf/dispatch [::events/set-question-battery-value idx v]))}]
                     [labelled-input/view
                      {:data-type   :data.type/text-simple
                       :full-width? true
                       :label       "External ID"
                       :value       (get-in opt [:survey-question-ext-id/external-id])
                       :on-blur     (fn [v]
                                      (rf/dispatch [::events/set-external-id-value idx v]))}]
                     [:div
                      {:class "flex flex-1 gap-1"}
                      [button/square-secondary-open
                       {:tooltip    (loc/tr :survey-block-editor/remove-external-id)
                        :aria-label (loc/tr :survey-block-editor/remove-external-id)
                        :on-click   #(rf/dispatch [::events/remove-ext-id idx])}
                       [:i.fa.fa-times-circle]]]])))]]
     [:div
      [button/sm-primary-solid
       {:on-click #(rf/dispatch [::events/add-external-ids])}
       [:i.fal.fa-plus]
       (loc/tr :add)]]])))

(defn creator-body
  []
  (let [selected-languages @(rf/subscribe [::sub/selected-languages])
        lang @(rf/subscribe [::sub/selected-language selected-languages])
        question-options @(rf/subscribe [::sub/numeric-questions-options])
        survey-question @(rf/subscribe [::sub/survey-question])
        data-type (:survey-question/data-type survey-question)
        lang-question (assoc survey-question :lang lang)]
    [:div
     {:class "flex flex-col gap-6 mx-auto w-5xl"}
     [text/h2
      (loc/tr :supported-languages)]
     [:div
      {:class "flex gap-2 flex-col"}
      [checkbox-group/view
       {:value       (set selected-languages)
        :options     (->> selected-languages
                          (mapv (fn [l]
                                  {:value     l
                                   :label     (languages/get-label l)
                                   :disabled? (= l :ENG)
                                   :checked?  true})))
        :on-change   (fn [languages]
                       (rf/dispatch [::events/set-languages languages]))
        :horizontal? true}]
      [:div
       [data-input/view
        {:data-type   :data.type/keyword
         :placeholder (loc/tr :add-language)
         :options     (->> languages/options
                           (remove (fn [{:keys [value]}]
                                     (some #{value} selected-languages))))
         :on-change   (fn [lang _lang-opt]
                        (rf/dispatch [::events/add-language lang]))}]]]
     [:fieldset
      {:class "flex flex-col gap-3"}
      [tab-strip/view
       {:selected-tab lang
        :tabs         (->> selected-languages
                           (mapv (fn [l]
                                   {:value l
                                    :label (languages/get-label l)})))
        :on-change!   (fn [tab]
                        (rf/dispatch [::events/set-language tab]))}]

      (when lang
        [:fieldset
         {:class "flex flex-col gap-3"}
         [:span
          {:class "text-bold"}
          (loc/tr :properties)]
         [localized-label-input lang-question]
         [data-type-select lang-question]

         (case data-type
           :data.type/file
           [file-meta-inputs lang-question]
           :data.type/calculated-number
           [calculation-rule-editor question-options lang-question]
           :data.type/ref-single
           [option-management lang-question]
           :data.type/ref-multi
           [option-management lang-question]
           :data.type/keyword
           [option-list-select lang-question]
           :data.type/keyword-multi
           [option-list-select lang-question]
           nil)

         (when-not (= data-type :data.type/file)
           [data-unit-input lang-question])

         ^{:key (str "survey-question-creator-localized-info-" lang)}
         [localized-info-input lang-question]

         [keywords-input lang-question]
         [external-id lang-question]
         [unique-identifier-input lang-question]

         (when (= data-type :data.type/file)
           [labelled-input/view
            {:label     (loc/tr :survey-question-file-name-alias)
             :data-type :data.type/text-simple
             :value     (:survey-question/file-name-abbreviation lang-question)
             :on-blur   (fn [v]
                          (rf/dispatch [::events/set-file-abbreviation v]))}])])]]))

(defn creator-footer
  []
  (let [unsaved-changes @(rf/subscribe [::sub/unsaved-changes])
        changes @(rf/subscribe [::sub/survey-question-changes])
        errors @(rf/subscribe [::sub/validate-errors])
        question-id (:db/id changes)]
    [:div
     {:class "flex mx-auto w-5xl justify-between"}
     [:div
      {:class "flex gap-3"}
      (if unsaved-changes
        [button/secondary-hollow
         {:on-click #(rf/dispatch [::survey-events/close-question-creator])}
         (loc/tr :close-without-saving)]
        [button/secondary-hollow
         {:on-click #(rf/dispatch [::survey-events/close-question-creator])}
         (loc/tr :close)])
      (when question-id
        [button/danger-solid
         {:on-click #(rf/dispatch [::events/delete-question])}
         (loc/tr :delete)])]
     [:div
      {:class "flex gap-3"}
      [button/primary-solid
       {:disabled? (or (not unsaved-changes)
                       errors)
        :tooltip   (when errors
                     (into [:ul]
                           (->> errors
                                (map (fn [err]
                                       [:ul
                                        {:class "flex flex-row gap-1"}
                                        [:i
                                         {:class "fa fa-exclamation-circle flex self-center font-sm text-danger-light-text"}]
                                        err]))
                                (doall))))
        :on-click  #(rf/dispatch [::events/save])}
       (loc/tr :save)]]]))

(defn view
  []
  ;; Both paths open this modal through the same `question-create-open?` flag,
  ;; so it used to say "Create survey question" while editing a saved one -
  ;; next to that question's own Delete button. An edit is the case where a
  ;; question was loaded into state and therefore has a `:db/id`.
  (let [editing? (some? (:db/id @(rf/subscribe [::sub/saved-survey-question])))]
    [modal/view
     {:visible? true
      :class    "h-full"
      :hide!    #(rf/dispatch [::survey-events/close-question-creator])
      :size     ::modal-style/full
      :z-index  60000}
     (if editing?
       (loc/tr :edit-survey-question)
       (loc/tr :create-survey-questions))
     [creator-body]
     [creator-footer]]))
